import React, { useMemo, useState } from 'react';
import { Users, GraduationCap, Heart, Shield, Plus, Eye, Edit2, PowerOff, Link as LinkIcon, School, Search, Filter, ChevronDown } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { DataTable } from '../shared/DataTable';
import { SlideOver } from '../../shared/SlideOver';
import { AdminTabs, AdminTab } from '../shared/AdminTabs';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { isLearnerAccount } from '../../../accounts';
import { adminDeleteLearner } from '../../../data/supabase/adminActions';
import { useBreakGlass } from '../shared/useBreakGlass';
import { BreakGlassGate } from '../shared/BreakGlassGate';
import { ServerDirectoryPanel } from './users/ServerDirectoryPanel';
import { AccountStatusNotice } from './users/AccountStatusNotice';
import { TeacherVerificationPanel } from './users/TeacherVerificationPanel';
import { useServerDirectory } from './users/useServerDirectory';
import { useUsersData } from './users/useUsersData';
import { getColumnsForRole, schoolColumns } from './users/columns';
import { UserDetailPanel } from './users/UserDetailPanel';
import { SchoolDetailPanel } from './users/SchoolDetailPanel';
import { AddUserForm } from './users/AddUserForm';
import { LinkUsersForm } from './users/LinkUsersForm';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { ROLE_CONFIG } from './users/types';
import type { UsersPageProps, RoleFilter, UserRow, SchoolData } from './users/types';
export function UsersPage({
  accounts: propsAccounts,
  getLearnerData: propsGetLearnerData,
  getLearnerModuleMastery,
  onNavigateToLearner
}: UsersPageProps) {
  const store = useAppStore();
  const accounts = propsAccounts || store.accounts;
  const getLearnerData = propsGetLearnerData || store.getLearnerData;
  // View state
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [selectedSchool, setSelectedSchool] = useState<SchoolData | null>(null);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<UserRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  // Eén ophaalactie, gedeeld door de tabel en het meldingspaneel.
  const directory = useServerDirectory();
  // Break glass — welke leerlingen mag deze beheerder nu inzien?
  const breakGlass = useBreakGlass();

  // Voor welke leerlingen KAN de database een venster openen? Alleen voor wie
  // daar een profiel heeft. Een lokaal aangemaakt account bestaat serverzijdig
  // niet, dus `request_data_access` faalt daar op de foreign key — en die fout
  // is niet te onderscheiden van "het mechanisme werkt niet".
  const serverLearnerIds = useMemo(() => new Set(directory.accounts.filter(a => a.role === 'learner').map(a => a.id)), [directory.accounts]);

  /**
   * Eén verwijderpad voor zowel de losse als de bulkactie.
   *
   * Leerlingaccounts gaan via `delete_learner_data(..., true)`: die functie
   * kent élke tabel waar gegevens van een kind in kunnen staan. Volwassen
   * accounts hebben nog geen serverzijdige verwijderroute, dus daar blijft het
   * bij de lokale kopie — en dat staat ook zo in de bevestiging. Beloven dat
   * iets van de server verdwijnt terwijl dat niet gebeurt, is erger dan de
   * beperking benoemen.
   */
  const removeAccount = async (row: UserRow): Promise<boolean> => {
    if (row.role === 'learner') {
      const res = await adminDeleteLearner(row.id);
      if (!res.ok) return false;
    }
    store.deleteAccount(row.id);
    return true;
  };
  // Data hooks
  const {
    users,
    allSchools,
    counts,
    filtered,
    filteredSchools,
    selectedMastery
  } = useUsersData({
    accounts,
    getLearnerData,
    roleFilter,
    searchQuery,
    schoolFilter,
    selectedUser,
    // Break glass. Zonder actief venster levert de hook geen leerdata, en
    // tonen tabel, detailpaneel en grafiek allemaal een streepje.
    hasDataAccess: breakGlass.has,
    // Accounts die alleen op de server bestaan verschijnen als extra rijen in
    // dezelfde tabel. Ze in een apart paneel laten staan betekende dat de
    // beheerder ze pas zag ná een klik op iets anders — de lijst is het
    // hoofdding, niet de melding.
    serverAccounts: directory.accounts
  });
  // Build tabs with counts
  const roleTabs: AdminTab[] = useMemo(() => [{
    id: 'all',
    label: `Alle (${counts.all})`,
    icon: <Users className="w-4 h-4" data-id="element-8039" data-mp="ub9njime1rdy" />
  }, {
    id: 'learner',
    label: `Leerlingen (${counts.learner})`,
    icon: <Users className="w-4 h-4" data-id="element-8040" data-mp="gfxtldg244y3" />
  }, {
    id: 'teacher',
    label: `Docenten (${counts.teacher})`,
    icon: <GraduationCap className="w-4 h-4" data-id="element-8041" data-mp="192a7okwhz9px" />
  }, {
    id: 'school',
    label: `Scholen (${counts.school})`,
    icon: <School className="w-4 h-4" data-id="element-8042" data-mp="1e13l6dbsb0q3" />
  }, {
    id: 'parent',
    label: `Ouders (${counts.parent})`,
    icon: <Heart className="w-4 h-4" data-id="element-8043" data-mp="zcz1p74wql69" />
  }, {
    id: 'admin',
    label: `Admins (${counts.admin})`,
    icon: <Shield className="w-4 h-4" data-id="element-8044" data-mp="14b0i1kay86e1" />
  }], [counts]);
  const openEditMode = (row: UserRow) => {
    setSelectedUser(row);
    setIsEditing(true);
  };
  const rowActions = [{
    label: 'Bekijken',
    icon: Eye,
    onClick: (row: UserRow) => {
      setSelectedUser(row);
      setIsEditing(false);
    }
  }, {
    label: 'Bewerken',
    icon: Edit2,
    onClick: openEditMode
  }, ...(onNavigateToLearner ? [{
    label: 'Open dashboard',
    icon: Eye,
    onClick: (row: UserRow) => row.role === 'learner' && onNavigateToLearner(row.id)
  }] : []), {
    label: 'Verwijderen',
    icon: PowerOff,
    onClick: (row: UserRow) => setDeleteConfirmUser(row),
    danger: true
  }];
  return <PageLayout title="Gebruikers" subtitle="Alle accounts — leerlingen, docenten, ouders en admins" breadcrumbs={[{
    label: 'Gebruikers'
  }, {
    label: 'Overzicht'
  }]} actions={<div className="flex gap-2" data-id="element-8046" data-mp="1iwfq3fsus9mr">
          <ConnectionBadge status="live" data-id="element-8047" data-mp="1dyeaj4umysg1" />
          <Button size="sm" variant="secondary" onClick={() => setIsLinkModalOpen(true)} data-id="element-8048" data-mp="1dzfkt73tvawp">
          
            <LinkIcon className="w-4 h-4 mr-1.5" data-id="element-8049" data-mp="18xzzddxqn2ds" />
            Koppelen
          </Button>
          <Button size="sm" onClick={() => setIsAddModalOpen(true)} data-id="element-8050" data-mp="14xoh27sule5h">
            <Plus className="w-4 h-4 mr-1.5" data-id="element-8051" data-mp="100fvryceku00" />
            Toevoegen
          </Button>
        </div>} data-id="element-8045" data-mp="1nope2qaeqzme">
      
      {/* Role filter tabs */}
      <AdminTabs tabs={roleTabs} activeTab={roleFilter} onTabChange={id => {
      setRoleFilter(id as RoleFilter);
      setSearchQuery('');
      setSchoolFilter('');
      setSelectedIds([]);
    }} data-id="element-8052" data-mp="3bouizc64x0jz" />
      

      {/* Bulk Actions Toolbar */}
      {selectedIds.length > 0 && <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4 flex items-center justify-between shadow-sm" data-id="element-8053" data-mp="3pl82sry55e1m">
          <div className="flex items-center gap-2 text-sm font-medium text-blue-900" data-id="element-8054" data-mp="2su5tzyys30qg">
            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full" data-id="element-8055" data-mp="2wy3q00st3vfa">
              {selectedIds.length}
            </span>
            geselecteerd
          </div>
          <div className="flex items-center gap-2" data-id="element-8056" data-mp="36of42g67fkzw">
            <Button size="sm" variant="secondary" onClick={() => setSelectedIds([])} className="bg-white" data-id="element-8057" data-mp="298j5vekx2s4f">
            
              Deselecteren
            </Button>
            <Button size="sm" variant="danger" onClick={() => setIsBulkDeleteConfirmOpen(true)} data-id="element-8058" data-mp="2e59l5zowdjos">
            
              <PowerOff className="w-4 h-4 mr-1.5" data-id="element-8059" data-mp="17lv2moqn1kl3" />
              Verwijderen
            </Button>
          </div>
        </div>}

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4" data-id="element-8060" data-mp="1glhryc63ezxs">
        <div className="relative flex-1 max-w-sm" data-id="element-8061" data-mp="1cjd11l714heu">
          {/*
             Het supportnummer staat IN de placeholder, niet in een tooltip.
             Zonder die vermelding weet een beheerder niet dat zoeken op naam bij
             een leerling per definitie niets oplevert (namen komen niet over de
             lijn), en dan lijkt de zoekfunctie kapot in plaats van afgeschermd.
            */}
          <Input type="text" placeholder={roleFilter === 'school' ? 'Zoek op schoolnaam...' : roleFilter === 'learner' ? 'Zoek op supportnummer (L-XXXXX)...' : 'Zoek op naam, e-mail of supportnummer...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-8063" data-mp="2487wbk5wz6lk" />} className="pl-9" data-id="element-8062" data-mp="1v92vu8asl36r" />
          
        </div>
        {roleFilter === 'learner' && allSchools.length > 0 && <div className="relative" data-id="element-8064" data-mp="2svp7m9wc5hi7">
            <Select value={schoolFilter} onChange={e => setSchoolFilter(e.target.value)} icon={<Filter className="w-4 h-4" data-id="element-8066" data-mp="2eumlxy99697t" />} options={allSchools.map(s => ({
          value: s,
          label: s
        }))} placeholder="Alle scholen" className="pl-9" fullWidth={false} data-id="element-8065" data-mp="2sunodi561ope" />
          
          </div>}
      </div>

      {/*
        De herkomst-statusbalk hoort BOVEN de tabel waar hij over gaat.
        Hij stond in het detailpaneel, en daar voegde hij niets toe: een
        beheerder die één account bekijkt, heeft niets aan het totaal aantal
        serveraccounts — en de melding "namen zijn afgeschermd" landde daar naast
        de gate die datzelfde al zegt. Bij de tabel is het wél informatie: hoeveel
        van deze rijen komen van de server, en lukte het ophalen.
       */}
      {roleFilter !== 'school' && <ServerDirectoryPanel directory={directory} onShowTeachers={() => setRoleFilter('teacher')} data-id="element-8067" data-mp="1r7wlh1cjympb" />}

      {/* Table */}
      {roleFilter === 'school' ? filteredSchools.length === 0 ? <div className="bg-white rounded-xl border border-gray-200 py-16 text-center shadow-sm" data-id="element-8068" data-mp="2usc4yh32if22">
            <School className="w-10 h-10 text-gray-200 mx-auto mb-3" data-id="element-8069" data-mp="2gskkpylyk2fm" />
            <p className="text-sm text-gray-500" data-id="element-8070" data-mp="2lpb0ektuow0i">Geen scholen gevonden</p>
          </div> : <DataTable columns={schoolColumns} data={filteredSchools} onRowClick={row => setSelectedSchool(row)} data-id="element-8071" data-mp="39fx3dxozzq3n" /> : filtered.length === 0 ? <div className="bg-white rounded-xl border border-gray-200 py-16 text-center shadow-sm" data-id="element-8072" data-mp="3dg7efornd6pd">
          <Users className="w-10 h-10 text-gray-200 mx-auto mb-3" data-id="element-8073" data-mp="2zosunfpx11h9" />
          <p className="text-sm text-gray-500" data-id="element-8074" data-mp="34ooxhbmvmmv8">Geen gebruikers gevonden</p>
          {(searchQuery || schoolFilter) && <button onClick={() => {
        setSearchQuery('');
        setSchoolFilter('');
      }} className="mt-2 text-xs text-blue-600 hover:underline" data-id="element-8075" data-mp="3sdzoi1efd2w6">
          
              Filters wissen
            </button>}
        </div> : <DataTable columns={getColumnsForRole(roleFilter)} data={filtered} onRowClick={row => {
      setSelectedUser(row);
      setIsEditing(false);
    }} rowActions={roleFilter !== 'school' ? rowActions : undefined} searchable={false} pagination={true} pageSize={25} selectable={roleFilter !== 'school'} selectedIds={selectedIds} onSelectionChange={setSelectedIds} data-id="element-8076" data-mp="adcjtz76wc0n" />}

      {/* User Detail SlideOver */}
      <SlideOver open={!!selectedUser} onClose={() => {
      setSelectedUser(null);
      setIsEditing(false);
    }} title={isEditing ? 'Gebruiker bewerken' : selectedUser?.name || ''} description={selectedUser && !isEditing ? ROLE_CONFIG[selectedUser.role].label : undefined} data-id="element-8077" data-mp="3nlpdxb07kimi">
        
        {bulkError && <div role="alert" className="p-3 mb-4 rounded-xl bg-rose-50 border border-rose-200 text-sm font-semibold text-rose-700" data-id="element-8078" data-mp="3sipamvb6490y">
          
            {bulkError}
          </div>}

        {/*
           De gate staat BOVEN het paneel en niet erin, zodat het paneel zelf
           niets van break glass hoeft te weten. Het toont gewoon wat het
           binnenkrijgt — en zonder venster krijgt het nulwaarden binnen.
            `unavailableReason` dekt het geval dat de gate wél verschijnt maar niet
           kan werken: een leerling die alleen op dit apparaat bestaat, heeft geen
           profiel op de server, dus de database kan er geen venster voor openen.
           Zonder die uitleg tikt een beheerder een reden in en krijgt een
           algemene foutmelding — en dan lijkt het mechanisme kapot in plaats van
           niet van toepassing.
          */}
        {/*
           Serverzijdige statussen (verificatie, toestemming) staan BOVEN de
           break-glass-gate. Ze gaan over of dit account überhaupt mag werken,
           en dat is een andere vraag dan of de beheerder de leerdata mag zien.
           Andersom zou een beheerder eerst een venster openen en pas daarna
           lezen dat de toestemming is ingetrokken.
          */}
        {selectedUser && !isEditing && <>
            <AccountStatusNotice account={directory.accounts.find(a => a.id === selectedUser.id)} data-id="element-8079" data-mp="av2i9wgkr0a0" />
            {(() => {
          const server = directory.accounts.find(a => a.id === selectedUser.id);
          return server ? <TeacherVerificationPanel account={server} onChanged={() => void directory.refresh()} data-id="element-8080" data-mp="zhebsry3pxm" /> : null;
        })()}
          </>}
        {selectedUser?.role === 'learner' && !isEditing && <BreakGlassGate learnerName={selectedUser.name} isOpen={breakGlass.has(selectedUser.id)} expiresAt={breakGlass.grants[selectedUser.id]?.expiresAt} unavailableReason={serverLearnerIds.has(selectedUser.id) ? undefined : 'Dit account bestaat alleen op dit apparaat en niet op de server. Een toegangsvenster wordt in de database vastgelegd, en dat kan niet voor een account dat daar niet staat. De gegevens die je hier ziet komen uit de lokale opslag.'} onOpen={reason => breakGlass.open(selectedUser.id, reason)} onClose={() => breakGlass.close(selectedUser.id)} data-id="element-8081" data-mp="3o3oxahfs6nup" />}
        {selectedUser && <UserDetailPanel user={selectedUser} isEditing={isEditing} selectedMastery={selectedMastery} accounts={accounts} store={store} onNavigateToLearner={onNavigateToLearner} onClose={() => setSelectedUser(null)} onStartEdit={openEditMode} onStopEdit={() => setIsEditing(false)} onUserUpdate={setSelectedUser} data-id="element-8082" data-mp="3p3z1yfxs3pss" />}
      </SlideOver>

      {/* School Detail SlideOver */}
      <SlideOver open={!!selectedSchool} onClose={() => setSelectedSchool(null)} title={selectedSchool?.name ?? ''} data-id="element-8083" data-mp="36bwk519ei3gy">
        
        {selectedSchool && <SchoolDetailPanel school={selectedSchool} data-id="element-8084" data-mp="3b9y2tl2x00rw" />}
      </SlideOver>

      {/* Add User Modal */}
      <SlideOver open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Nieuwe gebruiker" description="Maak een nieuw account aan" data-id="element-8085" data-mp="2xecelg4i9l0j">
        
        <AddUserForm onClose={() => setIsAddModalOpen(false)} data-id="element-8086" data-mp="vly3azwgvet3" />
      </SlideOver>

      {/* Link Users Modal */}
      <SlideOver open={isLinkModalOpen} onClose={() => setIsLinkModalOpen(false)} title="Accounts koppelen" description="Hoe een koppeling tot stand komt" data-id="element-8087" data-mp="zlyf0cs6lbwg">
        
        <LinkUsersForm onClose={() => setIsLinkModalOpen(false)} data-id="element-8088" data-mp="2g3gx3zeyk3cy" />
        
      </SlideOver>

      <ConfirmDialog open={deleteConfirmUser !== null} title="Account verwijderen"
    // De oude tekst zei "dit kan niet ongedaan worden gemaakt" en
    // suggereerde daarmee een definitieve verwijdering. Dat is onwaar
    // zolang `deleteAccount` een pure lokale store-mutatie is: op een
    // ander apparaat bestaat het account gewoon nog. Een beheerder die
    // dat gelooft, denkt dat hij een verwijderverzoek heeft afgehandeld.
    // Zeggen wat het wél doet, tot de dataLayer op de server draait.
    message={deleteConfirmUser ? [`${deleteConfirmUser.name} wordt van dit apparaat verwijderd.`, '', 'Let op: dit verwijdert het account nog niet van de server. Op een ander apparaat blijft het bestaan. Gebruik dit dus niet om een verwijderverzoek af te handelen.'].join('\n') : ''} confirmLabel="Verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={async () => {
      if (deleteConfirmUser) {
        await removeAccount(deleteConfirmUser);
      }
      setDeleteConfirmUser(null);
    }} onCancel={() => setDeleteConfirmUser(null)} data-id="element-8089" data-mp="2tuvk0hbzkopa" />
      

      <ConfirmDialog open={isBulkDeleteConfirmOpen} title="Meerdere accounts verwijderen" message={[`${selectedIds.length} accounts worden van dit apparaat verwijderd.`, '', 'Let op: dit verwijdert de accounts nog niet van de server. Op een ander apparaat blijven ze bestaan.'].join('\n')} confirmLabel="Verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={async () => {
      // Eén voor één en met foutafhandeling, niet `forEach` met een
      // promise erin. Die start dertig verwijderingen tegelijk, wacht op
      // geen enkele, en meldt "gelukt" ongeacht de uitkomst — precies de
      // stille bulkfout die eerder bij de leerlingimport zat.
      const failed: string[] = [];
      for (const id of selectedIds) {
        const row = users.find(u => u.id === id);
        if (!row) continue;
        const ok = await removeAccount(row);
        if (!ok) failed.push(row?.name ?? id);
      }
      setSelectedIds(failed.length ? selectedIds : []);
      setBulkError(failed.length ? `${failed.length} van de ${selectedIds.length} accounts konden niet worden verwijderd: ${failed.join(', ')}.` : null);
      setIsBulkDeleteConfirmOpen(false);
    }} onCancel={() => setIsBulkDeleteConfirmOpen(false)} data-id="element-8090" data-mp="3nivsh6rrc0qc" />
      
    </PageLayout>;
}